import React from 'react';
import { useDarkClasses } from '../../../../hooks/useDarkClasses';
import { SCIFI_MODULE_DATA } from '../genreModuleData';
import { SearchableChipField, SingleSelectField } from './shared';
const ACCENT = 'bg-cyan-100 text-cyan-700 border-cyan-500 dark:bg-cyan-900/30 dark:text-cyan-300';
// ═══════════════════════════════════════════════════
// FULL SCI-FI MODULE
// ═══════════════════════════════════════════════════
interface SciFiModuleProps {
  subtype: string;
  setSubtype: (v: string) => void;
  coreConcepts: string[];
  setCoreConcepts: React.Dispatch<React.SetStateAction<string[]>>;
  scale: string;
  setScale: (v: string) => void;
  scienceDepth: string;
  setScienceDepth: (v: string) => void;
  storyEngines: string[];
  setStoryEngines: React.Dispatch<React.SetStateAction<string[]>>;
}
export const SciFiModule: React.FC<SciFiModuleProps> = ({
  subtype,
  setSubtype,
  coreConcepts,
  setCoreConcepts,
  scale,
  setScale,
  scienceDepth,
  setScienceDepth,
  storyEngines,
  setStoryEngines
}) => {
  const dk = useDarkClasses();
  const toggleConcept = (c: string) => setCoreConcepts(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]);
  const toggleEngine = (e: string) => setStoryEngines(prev => prev.includes(e) ? prev.filter(x => x !== e) : [...prev, e]);
  return <div className="space-y-8" data-id="element-21382" data-mp="2snsr16i9xvi2">
      {/* Sci-Fi Subtype */}
      <SingleSelectField label="Sci-Fi Subtype" placeholder="e.g. Space Opera, Cyberpunk, Dystopian..." items={SCIFI_MODULE_DATA.subtypes} value={subtype} onChange={setSubtype} accentClass={ACCENT} data-id="element-21383" data-mp="3gclnd42t65d7" />

      {/* Core Concept */}
      <SearchableChipField label="Core Concept" placeholder="e.g. Artificial Intelligence, Time Travel, First Contact..." items={SCIFI_MODULE_DATA.coreConcepts} selected={coreConcepts} onToggle={toggleConcept} accentClass={ACCENT} data-id="element-21384" data-mp="3bdijhmyke56t" />

      {/* Scale */}
      <div data-id="element-21385" data-mp="1fx0lt10jv55v">
        <label className={`block text-sm font-medium mb-2 ${dk.textSecondary}`} data-id="element-21386" data-mp="1fwqyxmasxe4e">
          Scale
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-21387" data-mp="1bw708eaky2g5">
          {SCIFI_MODULE_DATA.scales.map(s => {
          const isSelected = scale === s.level;
          return <button key={s.level} onClick={() => setScale(isSelected ? '' : s.level)} className={`p-3 rounded-xl border text-left transition-all
                  ${isSelected ? 'bg-cyan-600 text-white border-cyan-600 dark:bg-cyan-400 dark:text-cyan-950' : `${dk.surfaceMuted} ${dk.border} hover:border-cyan-400`}`} data-id="element-21388" data-mp="124b1d10o68av">
                <div className={`font-bold text-sm mb-1 ${isSelected ? '' : dk.textPrimary}`} data-id="element-21389" data-mp="j79tn8fqdu38">
                  {s.level}
                </div>
                <div className={`text-xs ${isSelected ? 'opacity-90' : dk.textMuted}`} data-id="element-21390" data-mp="2djpws0d8cs3v">
                  {s.description}
                </div>
              </button>;
        })}
        </div>
      </div>

      {/* Science Depth */}
      <div data-id="element-21391" data-mp="2utlfj62v0l36">
        <label className={`block text-sm font-medium mb-2 ${dk.textSecondary}`} data-id="element-21392" data-mp="2uttur4mgg2hj">
          Science Depth
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-21393" data-mp="2zsy1zpptjq58">
          {SCIFI_MODULE_DATA.scienceDepths.map(sd => {
          const isSelected = scienceDepth === sd.level;
          return <button key={sd.level} onClick={() => setScienceDepth(isSelected ? '' : sd.level)} className={`p-3 rounded-xl border text-left transition-all
                  ${isSelected ? 'bg-cyan-600 text-white border-cyan-600 dark:bg-cyan-400 dark:text-cyan-950' : `${dk.surfaceMuted} ${dk.border} hover:border-cyan-400`}`} data-id="element-21394" data-mp="27526upfwe6ak">
                <div className={`font-bold text-sm mb-1 ${isSelected ? '' : dk.textPrimary}`} data-id="element-21395" data-mp="3rj2sm64699ew">
                  {sd.level}
                </div>
                <div className={`text-xs ${isSelected ? 'opacity-90' : dk.textMuted}`} data-id="element-21396" data-mp="3l3u8tm4073en">
                  {sd.description}
                </div>
              </button>;
        })}
        </div>
      </div>

      {/* Story Engine */}
      <SearchableChipField label="Story Engine" placeholder="e.g. Exploration, Survival, Rebellion..." items={SCIFI_MODULE_DATA.storyEngines} selected={storyEngines} onToggle={toggleEngine} accentClass={ACCENT} data-id="element-21397" data-mp="3v44kv3eay4s8" />
    </div>;
};
// ═══════════════════════════════════════════════════
// COMPACT CROSSOVER
// ═══════════════════════════════════════════════════
interface SciFiModuleCompactProps {
  subtype: string;
  setSubtype: (v: string) => void;
  coreConcepts: string[];
  setCoreConcepts: React.Dispatch<React.SetStateAction<string[]>>;
  scale: string;
  setScale: (v: string) => void;
}
export const SciFiModuleCompact: React.FC<SciFiModuleCompactProps> = ({
  subtype,
  setSubtype,
  coreConcepts,
  setCoreConcepts,
  scale,
  setScale
}) => {
  const dk = useDarkClasses();
  const toggleConcept = (c: string) => setCoreConcepts(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]);
  return <div className="space-y-5" data-id="element-21398" data-mp="169uuufmcg1cr">
      <SingleSelectField label="Sci-Fi Subtype" placeholder="Search subtypes..." items={SCIFI_MODULE_DATA.subtypes} value={subtype} onChange={setSubtype} accentClass={ACCENT} data-id="element-21399" data-mp="rj41h353su7j" />

      <SearchableChipField label="Core Concept" placeholder="Search concepts..." items={SCIFI_MODULE_DATA.coreConcepts} selected={coreConcepts} onToggle={toggleConcept} accentClass={ACCENT} data-id="element-21400" data-mp="ijh18ko41ug2" />

      <div data-id="element-21401" data-mp="8mcfgly6g0zs">
        <label className={`block text-sm font-medium mb-2 ${dk.textSecondary}`} data-id="element-21402" data-mp="ditdq8z3bvql">
          Scale
        </label>
        <div className="grid grid-cols-2 gap-2" data-id="element-21403" data-mp="2f8s89fe5ckyy">
          {SCIFI_MODULE_DATA.scales.map(s => {
          const isSelected = scale === s.level;
          return <button key={s.level} onClick={() => setScale(isSelected ? '' : s.level)} className={`p-2.5 rounded-xl border text-left transition-all
                  ${isSelected ? 'bg-cyan-600 text-white border-cyan-600 dark:bg-cyan-400 dark:text-cyan-950' : `${dk.surfaceMuted} ${dk.border}`}`} data-id="element-21404" data-mp="26fybg42np2xc">
                <div className={`font-bold text-xs mb-0.5 ${isSelected ? '' : dk.textPrimary}`} data-id="element-21405" data-mp="2u6100a7d3qr7">
                  {s.level}
                </div>
                <div className={`text-[10px] ${isSelected ? 'opacity-90' : dk.textMuted}`} data-id="element-21406" data-mp="2k6a8mhe3lrvc">
                  {s.description}
                </div>
              </button>;
        })}
        </div>
      </div>
    </div>;
};